import { Link } from "react-router-dom";
import { Facebook, Linkedin, Instagram, Mail, MapPin, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import logoWebp from "@/assets/aiswerve-192.webp";
import logo from "@/assets/aiswerve.png";
import { NewsletterSubscription } from "@/components/NewsletterSubscription";

export const Footer = () => {
  return (
    <footer className="bg-secondary text-secondary-foreground">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* About Section */}
          <div className="space-y-4">
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-12 h-12 rounded-lg overflow-hidden flex items-center justify-center">
                <picture>
                  <source srcSet={logoWebp} type="image/webp" />
                  <img
                    src={logo}
                    alt="AISWERVE Logo"
                    width={48}
                    height={48}
                    className="w-full h-full object-contain"
                    loading="lazy"
                  />
                </picture>
              </div>
              <span className="font-montserrat font-bold text-xl text-foreground">
                AISWERVEAFRICA
              </span>
            </Link>
            <p className="font-roboto text-sm leading-relaxed">
              Equipping corporate professionals, developers, and business leaders with practical AI and digital skills for the future of work.
            </p>
            <div className="flex space-x-4">
              <Button variant="ghost" size="icon" className="hover:bg-primary/20" asChild>
                <a href="https://www.facebook.com/share/18NPYiK319/" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                  <Facebook className="h-4 w-4" />
                  <span className="sr-only">Facebook</span>
                </a>
              </Button>
              <Button variant="ghost" size="icon" className="hover:bg-primary/20" asChild>
                <a href="https://x.com/aiswerve_africa" target="_blank" rel="noopener noreferrer" aria-label="X (formerly Twitter)">
                  <svg className="h-4 w-4 fill-current" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                  <span className="sr-only">X (formerly Twitter)</span>
                </a>
              </Button>
              <Button variant="ghost" size="icon" className="hover:bg-primary/20" asChild>
                <a href="https://www.linkedin.com/in/aiswerve-africa-934404410/?lipi=urn%3Ali%3Apage%3Ad_flagship3_profile_view_base_contact_details%3Bjb1CQ4pbSvW9Egnps4qEhw%3D%3D" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                  <Linkedin className="h-4 w-4" />
                  <span className="sr-only">LinkedIn</span>
                </a>
              </Button>
              <Button variant="ghost" size="icon" className="hover:bg-primary/20" asChild>
                <a href="https://www.instagram.com/aiswerve_africa?igsh=MTllam1hbWFlbDRyZw==" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                  <Instagram className="h-4 w-4" />
                  <span className="sr-only">Instagram</span>
                </a>
              </Button>
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h3 className="font-montserrat font-semibold text-lg">Quick Links</h3>
            <ul className="space-y-2 font-roboto text-sm">
              <li><Link to="/about" className="hover:text-primary transition-colors">About Us</Link></li>
              <li><Link to="/programs" className="hover:text-primary transition-colors">Programs</Link></li>
              <li><Link to="/impact" className="hover:text-primary transition-colors">Our Impact</Link></li>
              <li><Link to="/get-involved" className="hover:text-primary transition-colors">Get Involved</Link></li>
              <li><Link to="/resources" className="hover:text-primary transition-colors">Resources</Link></li>
              <li><Link to="/podcast" className="hover:text-primary transition-colors">Podcast</Link></li>
            </ul>
          </div>

          {/* Programs */}
          <div className="space-y-4">
            <h3 className="font-montserrat font-semibold text-lg">Programs</h3>
            <ul className="space-y-2 font-roboto text-sm">
              <li><Link to="/programs#corporates" className="hover:text-primary transition-colors">Corporates & Enterprises</Link></li>
              <li><Link to="/programs#executives" className="hover:text-primary transition-colors">Business Leaders & Executives</Link></li>
              <li><Link to="/programs#startups" className="hover:text-primary transition-colors">Entrepreneurs & Startups</Link></li>
              <li><Link to="/programs#workforce" className="hover:text-primary transition-colors">Professionals & Workforce</Link></li>
              <li><Link to="/student-talent-accelerator" className="hover:text-primary transition-colors">Student Accelerator</Link></li>
              <li><Link to="/events" className="hover:text-primary transition-colors">Upcoming Events</Link></li>
            </ul>
          </div>

          {/* Contact & Newsletter */}
          <div className="space-y-4">
            <h3 className="font-montserrat font-semibold text-lg">Stay Connected</h3>
            <div className="space-y-3 font-roboto text-sm">
              <div className="flex items-center space-x-2">
                <Mail className="h-4 w-4 text-primary" />
                <a href="mailto:inquiries@aiswerveafrica.com" className="hover:text-primary transition-colors">inquiries@aiswerveafrica.com</a>
              </div>
              <div className="flex items-center space-x-2">
                <Phone className="h-4 w-4 text-primary" />
                <a href="tel:+254780490206" className="hover:text-primary transition-colors">+254 (07) 804 902 06</a>
              </div>
              <div className="flex items-center space-x-2">
                <MapPin className="h-4 w-4 text-primary" />
                <span>Nairobi, Kenya</span>
              </div>
            </div>
            
            {/* Newsletter Signup */}
            <NewsletterSubscription variant="footer" />
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-white/20 mt-8 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="font-roboto text-sm">
              {/* get current year */}
              © {new Date().getFullYear()} AISWERVE AFRICA Network. All rights reserved.
            </p>
            <div className="flex space-x-6 font-roboto text-sm">
              <Link to="/privacy" className="hover:text-primary transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-primary transition-colors">Terms of Service</Link>
              <Link to="/cookies" className="hover:text-primary transition-colors">Cookie Policy</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};